'use client';

import Link from 'next/link'
import Image from 'next/image'

export function MarketingFooter() {
    return (
        <footer className="border-t border-white/[0.06] bg-black relative z-10 overflow-hidden">
            {/* Top footer */}
            <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 py-16 2xl:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 2xl:gap-24">

                    {/* Brand */}
                    <div className="lg:col-span-2 space-y-4">
                        <Link href="/" className="flex items-center gap-2 mb-4 group">
                            <Image src="/logo.png" alt="Lenklyst Logo" width={32} height={32} className="object-contain transition-transform duration-300" style={{ width: '32px', height: '32px' }} />
                            <span className="text-white/20 font-light mx-0.5 select-none">|</span>
                            <span className="text-xl font-bold tracking-tighter text-white group-hover:text-primary transition-colors">
                                Lenklyst
                            </span>
                        </Link>
                        <p className="text-white/60 text-sm leading-relaxed max-w-xs">
                            A marketing and branding ecosystem for marketers, creators, startups and businesses to own their online presence.
                        </p>
                    </div>

                    {/* Links Sections - Always 5 columns */}
                    <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-6 gap-6 sm:gap-4 xl:gap-8">
                        {/* Product links */}
                        <div className="space-y-4">
                            <h3 className="text-white/80 font-semibold text-[10px] sm:text-xs tracking-widest uppercase">Product</h3>
                            <ul className="space-y-3">
                                { [
                                    { label: 'Smart Links', href: '/link-shortener' },
                                    { label: 'Link-in-Bio', href: '/link-in-bio' },
                                    { label: 'QR Generator', href: '/qr-code-generator' },
                                    { label: 'Forms & Polls', href: '/form-builder' },
                                    { label: 'Digital Store', href: '/digital-product-store' },
                                    { label: 'Referral System', href: '/referral-marketing' },
                                ].map((l) => (
                                    <li key={l.label}>
                                        <Link href={l.href} className="text-white/50 hover:text-white text-[11px] sm:text-xs transition-colors">{l.label}</Link>
                                    </li>
                                )) }
                            </ul>
                        </div>

                        {/* Company links */}
                        <div className="space-y-4">
                            <h3 className="text-white/80 font-semibold text-[10px] sm:text-xs tracking-widest uppercase">Company</h3>
                            <ul className="space-y-3">
                                {[
                                    { label: 'About Us', href: '/about' },
                                    { label: 'Pricing', href: '/pricing' },
                                    { label: 'How It Works', href: '/how-it-works' },
                                    { label: 'Contact', href: '/contact' },
                                    { label: 'Blog', href: '/blog' },
                                    { label: 'Guides', href: '/guides' },
                                ].map((l) => (
                                    <li key={l.label}>
                                        <Link href={l.href} className="text-white/50 hover:text-white text-[11px] sm:text-xs transition-colors">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Use Cases links */}
                        <div className="space-y-4">
                            <h3 className="text-white/80 font-semibold text-[10px] sm:text-xs tracking-widest uppercase">Use Cases</h3>
                            <ul className="space-y-3">
                                {[
                                    { label: 'For Creators', href: '/for-creators' },
                                    { label: 'For Startups', href: '/for-startups' },
                                    { label: 'For Founders', href: '/for-founders' },
                                    { label: 'For Agencies', href: '/for-agencies' },
                                    { label: 'For Small Biz', href: '/for-small-businesses' },
                                ].map((l) => (
                                    <li key={l.label}>
                                        <Link href={l.href} className="text-white/50 hover:text-white text-[11px] sm:text-xs transition-colors">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Compare links */}
                        <div className="space-y-4">
                            <h3 className="text-white/80 font-semibold text-[10px] sm:text-xs tracking-widest uppercase">Compare</h3>
                            <ul className="space-y-3">
                                {[
                                    { label: 'vs Bitly', href: '/vs/bitly' },
                                    { label: 'vs Linktree', href: '/vs/linktree' },
                                    { label: 'vs Beacons', href: '/vs/beacons' },
                                    { label: 'vs Carrd', href: '/vs/carrd' },
                                    { label: 'vs Taplink', href: '/vs/taplink' },
                                ].map((l) => (
                                    <li key={l.label}>
                                        <Link href={l.href} className="text-white/50 hover:text-white text-[11px] sm:text-xs transition-colors">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Resources links */}
                        <div className="space-y-4">
                            <h3 className="text-white/80 font-semibold text-[10px] sm:text-xs tracking-widest uppercase">Docs</h3>
                            <ul className="space-y-3">
                                {[
                                    { label: 'API Reference', href: '/docs/api' },
                                    { label: 'QR Code Docs', href: '/docs/qr-code' },
                                    { label: 'Referral Engine', href: '/docs/referral' },
                                    { label: 'Analytics', href: '/docs/analytics' },
                                    { label: 'All Docs', href: '/docs' },
                                ].map((l) => (
                                    <li key={l.label}>
                                        <Link href={l.href} className="text-white/50 hover:text-white text-[11px] sm:text-xs transition-colors">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Legal links */}
                        <div className="space-y-4">
                            <h3 className="text-white/80 font-semibold text-[10px] sm:text-sm tracking-widest uppercase">Legal</h3>
                            <ul className="space-y-3">
                                {[
                                    { label: 'Privacy', href: '/legal/privacy' },
                                    { label: 'Terms', href: '/legal/terms' },
                                    { label: 'Cookies', href: '/legal/cookies' },
                                    { label: 'Security', href: '/legal/security' },
                                ].map((l) => (
                                    <li key={l.label}>
                                        <Link href={l.href} className="text-white/50 hover:text-white text-[11px] sm:text-sm transition-colors">{l.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-white/[0.04] px-6 lg:px-12 py-6">
                <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-white/50 font-mono text-xs uppercase tracking-widest">
                        &copy; {new Date().getFullYear()} Lenklyst All rights reserved.
                    </p>
                    <p className="text-white/50 font-mono text-xs uppercase tracking-widest">
                        MARKETING & BRANDING ECOSYSTEM
                    </p>
                </div>
            </div>
        </footer>
    )
}
